import React, { useContext, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import { Select, SelectOption } from "../../../components/ui/select";
import PageHeader from "../../../components/shared/PageHeader";
import { updateInstoreFlips } from "../../../reducers/instore.slice";
import { AuthContext } from "../../../context/authContext";
import useMediaQuery from "../../../components/shared/useMediaQuery";

const INTEGER_KEYS = ["Backspace","Delete","ArrowLeft","ArrowRight","Tab","Enter","Home","End"];
const PRICE_KEYS = [...INTEGER_KEYS, "."];

const blockInvalidInt = (e) => {
  const bad = ["e","E",".",",","+","-"];
  if (bad.includes(e.key) || (!/^\d$/.test(e.key) && !INTEGER_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
};
const blockInvalidPrice = (e) => {
  const bad = ["e","E",",","+","-"];
  if (e.key === "." && e.target.value.includes(".")) { e.preventDefault(); return; }
  if (bad.includes(e.key) || (!/^\d$/.test(e.key) && e.key !== "." && !PRICE_KEYS.includes(e.key) && !e.ctrlKey && !e.metaKey)) e.preventDefault();
};

const EditInstoreFlipsPage = () => {
  const isMobile = useMediaQuery("(max-width: 640px)");
  const [showLoader, setShowLoader] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const instoreDetails = location.state.instoreDetails;

  const [formErrors, setFormErrors] = useState({ max_per_user_quantity: "", total_quantity: "", buy_price: "", cashout_price: "", upc: "" });
  const [formData, setFormData] = useState({
    item_name: instoreDetails?.item_name ?? "",
    item_link: instoreDetails?.item_link ?? "",
    upc: instoreDetails?.upc ?? "",
    retailer_name: instoreDetails?.retailer_name ?? "",
    total_quantity: instoreDetails?.total_quantity ?? "",
    description: instoreDetails?.description ?? "",
    tags: instoreDetails?.tags ?? "",
    buy_price: instoreDetails?.buy_price ?? "",
    cashout_price: instoreDetails?.cashout_price ?? "",
    max_per_user_quantity: instoreDetails?.max_per_user_quantity ?? "",
    status: instoreDetails?.status ?? "ACTIVE",
  });

  const { showToast } = useContext(AuthContext);

  useEffect(() => {
    if (formData.total_quantity < instoreDetails?.available_quantity) {
      setFormErrors((p) => ({ ...p, total_quantity: "Total quantity cannot be lower than approved quantity" }));
    }
    if (formData.max_per_user_quantity > formData.total_quantity) {
      setFormErrors((p) => ({ ...p, max_per_user_quantity: "Max per user quantity cannot exceed total quantity" }));
    }
  }, []);

  const handleFormData = (e) => {
    const { name, value } = e.target;

    if (name === "upc") {
      const v = value.replace(/[^0-9]/g, "");
      const err = v.length > 0 && (v.length < 11 || v.length > 14) ? "UPC should be 11–14 digits long" : v !== value ? "UPC must contain only numbers" : "";
      setFormErrors((p) => ({ ...p, upc: err }));
      setFormData((p) => ({ ...p, upc: v }));
      return;
    }

    if (["max_per_user_quantity", "total_quantity"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseInt(value, 10);
      if (isNaN(parsed) || parsed <= 0 || parsed !== Number(value)) { setFormErrors((p) => ({ ...p, [name]: "Must be a positive whole number (min 1)" })); return; }
      setFormData((p) => {
        const next = { ...p, [name]: parsed };
        if (name === "total_quantity") setFormErrors((pe) => ({ ...pe, [name]: "", max_per_user_quantity: next.max_per_user_quantity > parsed ? "Max per user quantity cannot exceed total quantity" : "" }));
        else setFormErrors((pe) => ({ ...pe, [name]: parsed > Number(p.total_quantity) ? "Max per user quantity cannot exceed total quantity" : "" }));
        return next;
      });
      return;
    }

    if (["buy_price", "cashout_price"].includes(name)) {
      if (value === "") { setFormData((p) => ({ ...p, [name]: "" })); setFormErrors((p) => ({ ...p, [name]: "" })); return; }
      const parsed = parseFloat(value);
      if (name === "buy_price" && (isNaN(parsed) || parsed <= 0)) { setFormErrors((p) => ({ ...p, buy_price: "Buy price must be greater than zero" })); return; }
      if (name === "cashout_price" && (isNaN(parsed) || parsed < 0)) { setFormErrors((p) => ({ ...p, cashout_price: "Must be a positive number" })); return; }
      setFormData((p) => ({ ...p, [name]: value }));
      setFormErrors((p) => ({ ...p, [name]: "" }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = () => {
    setShowLoader(true);
    const payload = { ...formData, id: instoreDetails.id };
    if (!payload.max_per_user_quantity || payload.max_per_user_quantity === "0" || payload.max_per_user_quantity === 0) {
      delete payload.max_per_user_quantity;
    }
    dispatch(updateInstoreFlips(payload)).then((res) => {
      setShowLoader(false);
      if (res?.error && Object.keys(res.error).length > 0) { showToast(res.error.message, "error"); return; }
      navigate("/dashboard/instore-flips");
    });
  };

  const isFormIncomplete =
    !formData.item_link || !formData.item_name || !formData.upc || !formData.cashout_price ||
    !formData.retailer_name || !formData.description || formData.buy_price === "" ||
    parseFloat(formData.buy_price) <= 0 || !formData.total_quantity ||
    !!formErrors.max_per_user_quantity || !!formErrors.upc || !!formErrors.buy_price;

  return (
    <div style={{ maxWidth: 600, margin: "0 auto", width: "100%", padding: "0 0 80px" }}>
      <div style={{ marginBottom: 4 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} style={{ paddingLeft: 0, gap: 6, color: "#8b9296" }}>
          <ArrowLeft size={15} /> Back
        </Button>
      </div>
      <div style={{ marginBottom: 24 }}>
        <PageHeader
          title="Edit Instore Flip"
          subtitle="Update the Instore Flip item details below."
          actions={
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
              <Button disabled={isFormIncomplete || showLoader} onClick={handleSubmit}>Update</Button>
            </div>
          }
        />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <Label htmlFor="upc">UPC</Label>
          <Input id="upc" name="upc" value={formData.upc} placeholder="Enter UPC" onChange={handleFormData} onWheel={(e) => e.target.blur()} />
          {formErrors.upc && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.upc}</p>}
        </div>
        <div>
          <Label htmlFor="item_name">Item Name</Label>
          <Input id="item_name" name="item_name" value={formData.item_name} placeholder="Item Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="item_link">Item Link</Label>
          <Input id="item_link" name="item_link" value={formData.item_link} placeholder="Enter Product Link" onChange={handleFormData} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="total_quantity">Quantity</Label>
            <Input id="total_quantity" type="number" name="total_quantity" value={formData.total_quantity} placeholder="Quantity" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.total_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.total_quantity}</p>}
          </div>
          <div>
            <Label htmlFor="max_per_user_quantity">Max Qty Per User</Label>
            <Input id="max_per_user_quantity" type="number" name="max_per_user_quantity" value={formData.max_per_user_quantity} placeholder="Max Qty" onChange={handleFormData} onKeyDown={blockInvalidInt} onPaste={(e) => { if (!/^\d+$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.max_per_user_quantity && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.max_per_user_quantity}</p>}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <div>
            <Label htmlFor="buy_price">Buy Price</Label>
            <Input id="buy_price" type="number" name="buy_price" value={formData.buy_price} placeholder="Buy Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.buy_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.buy_price}</p>}
          </div>
          <div>
            <Label htmlFor="cashout_price">Cash-Out Price</Label>
            <Input id="cashout_price" type="number" name="cashout_price" value={formData.cashout_price} placeholder="Cash-Out Price" onChange={handleFormData} onKeyDown={blockInvalidPrice} onPaste={(e) => { if (!/^\d*\.?\d*$/.test(e.clipboardData.getData("text"))) e.preventDefault(); }} onWheel={(e) => e.target.blur()} />
            {formErrors.cashout_price && <p style={{ fontSize: 11, color: "#f27059", marginTop: 4 }}>{formErrors.cashout_price}</p>}
          </div>
        </div>
        <div>
          <Label htmlFor="retailer_name">Retailer Name</Label>
          <Input id="retailer_name" name="retailer_name" value={formData.retailer_name} placeholder="Enter Retailer Name" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" name="description" value={formData.description} placeholder="Enter Description" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="tags">Tags</Label>
          <Input id="tags" name="tags" value={formData.tags} placeholder="Enter Tags" onChange={handleFormData} />
        </div>
        <div>
          <Label htmlFor="status">Status</Label>
          <Select id="status" name="status" value={formData.status} onChange={handleFormData}>
            <SelectOption value="ACTIVE">ACTIVE</SelectOption>
            <SelectOption value="INACTIVE">INACTIVE</SelectOption>
          </Select>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
        <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
        <Button disabled={isFormIncomplete || showLoader} onClick={handleSubmit}>Update</Button>
      </div>
    </div>
  );
};

export default EditInstoreFlipsPage;
